import clsx from 'clsx';
import { useEffect, useState } from 'react';
import {
  AlertTriangle,
  Check,
  HelpCircle,
  Pencil,
  Sparkles,
  X,
  type LucideIcon,
} from 'lucide-react';

import {
  CandidateChangeKinds,
  CandidateFieldStatuses,
  ConfidenceBuckets,
  type CandidateFieldResponse,
  type CandidateTableColumnRequest,
  type ModifyCandidateFieldRequest,
} from '../api';
import type { FieldDefinitionResponse } from '../../../schema/api';
import { CandidateFieldEditModal } from './CandidateFieldEditModal';
import { CandidateFieldColumnsEditor } from './CandidateFieldColumnsEditor';

export interface CandidateFieldCardProps {
  field: CandidateFieldResponse;
  busy?: boolean;
  onAccept: (fieldId: string) => void;
  onReject: (fieldId: string) => void;
  onModify: (fieldId: string, request: ModifyCandidateFieldRequest) => void;
  /**
   * The corresponding field from the source schema, for before→after diff rendering.
   * Only passed for Modified fields in re-education drafts; null otherwise.
   */
  sourceField?: FieldDefinitionResponse | null;
}

function confidenceClasses(confidence: string): string {
  switch (confidence) {
    case ConfidenceBuckets.High:
      return 'bg-emerald-100 text-emerald-800';
    case ConfidenceBuckets.Medium:
      return 'bg-amber-100 text-amber-800';
    case ConfidenceBuckets.Low:
      return 'bg-rose-100 text-rose-800';
    default:
      return 'bg-slate-100 text-slate-700';
  }
}

function confidenceIcon(confidence: string): LucideIcon {
  switch (confidence) {
    case ConfidenceBuckets.High:
      return Sparkles;
    case ConfidenceBuckets.Medium:
      return HelpCircle;
    case ConfidenceBuckets.Low:
      return AlertTriangle;
    default:
      return HelpCircle;
  }
}

function statusClasses(status: string): string {
  switch (status) {
    case CandidateFieldStatuses.Accepted:
      return 'bg-emerald-100 text-emerald-800';
    case CandidateFieldStatuses.Modified:
      return 'bg-sky-100 text-sky-800';
    case CandidateFieldStatuses.Rejected:
      return 'bg-slate-200 text-slate-600';
    case CandidateFieldStatuses.Proposed:
    default:
      return 'bg-slate-100 text-slate-700';
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case CandidateFieldStatuses.Accepted:
      return 'Aceptado';
    case CandidateFieldStatuses.Modified:
      return 'Modificado';
    case CandidateFieldStatuses.Rejected:
      return 'Rechazado';
    case CandidateFieldStatuses.Proposed:
    default:
      return 'Propuesto';
  }
}

function changeKindClasses(changeKind: string): string {
  switch (changeKind) {
    case CandidateChangeKinds.New:
      return 'bg-emerald-100 text-emerald-800';
    case CandidateChangeKinds.Modified:
      return 'bg-amber-100 text-amber-800';
    case CandidateChangeKinds.RemovalSuggested:
      return 'bg-rose-100 text-rose-800';
    case CandidateChangeKinds.Unchanged:
    default:
      return 'bg-slate-100 text-slate-500';
  }
}

function changeKindLabel(changeKind: string): string {
  switch (changeKind) {
    case CandidateChangeKinds.New:
      return 'nuevo';
    case CandidateChangeKinds.Modified:
      return 'modificado';
    case CandidateChangeKinds.RemovalSuggested:
      return 'eliminar';
    case CandidateChangeKinds.Unchanged:
    default:
      return 'sin cambios';
  }
}

/**
 * Card for a single candidate field. The expanded view shows the structural
 * evidence collected by the engine — labels, locations, counts — never any
 * sampled values (`FieldEvidence` carries only metadata by design).
 */
export function CandidateFieldCard({
  field,
  busy = false,
  onAccept,
  onReject,
  onModify,
  sourceField = null,
}: CandidateFieldCardProps): JSX.Element {
  const [expanded, setExpanded] = useState<boolean>(false);
  const [editing, setEditing] = useState<boolean>(false);
  const [hintDraft, setHintDraft] = useState<string>(field.hint ?? '');

  // Keep the textarea in sync when the parent refetches the draft and pushes a
  // new hint value (e.g. another tab edited it, or the proposal was reapplied).
  useEffect(() => {
    setHintDraft(field.hint ?? '');
  }, [field.hint]);

  const isRejected = field.status === CandidateFieldStatuses.Rejected;
  const ConfidenceIcon = confidenceIcon(field.confidence);

  const handleHintBlur = (): void => {
    const persisted = (field.hint ?? '').trim();
    const next = hintDraft.trim();
    if (next === persisted) {
      return;
    }
    // Empty value clears the hint server-side (trim → whitespace-only → null).
    onModify(field.id, { hint: hintDraft });
  };

  return (
    <div
      className={clsx(
        'card overflow-hidden p-0 transition',
        isRejected && 'opacity-60',
      )}
      data-testid={`candidate-field-${field.id}`}
    >
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <button
          type="button"
          className="flex-1 text-left"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-base font-semibold text-slate-900">
              {field.functionalName}
            </span>
            <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-700">
              {field.suggestedType}
            </span>
            <span
              data-testid="confidence-badge"
              className={clsx(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
                confidenceClasses(field.confidence),
              )}
            >
              <ConfidenceIcon className="h-3 w-3" aria-hidden strokeWidth={2.25} />
              <span className="hidden sm:inline">Confianza:&nbsp;</span>
              {field.confidence}
            </span>
            <span
              data-testid="status-pill"
              className={clsx(
                'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                statusClasses(field.status),
              )}
            >
              {statusLabel(field.status)}
            </span>
            {field.isCritical ? (
              <span className="inline-flex items-center rounded-full bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-700">
                Crítico
              </span>
            ) : null}
            {field.changeKind != null ? (
              <span
                data-testid="change-kind-badge"
                className={clsx(
                  'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                  changeKindClasses(field.changeKind),
                  // Unchanged (and any unknown future kind, which labels as "sin cambios") stays
                  // muted so only real diffs draw the eye.
                  field.changeKind !== CandidateChangeKinds.Modified &&
                    field.changeKind !== CandidateChangeKinds.New &&
                    field.changeKind !== CandidateChangeKinds.RemovalSuggested &&
                    'opacity-60',
                )}
              >
                {changeKindLabel(field.changeKind)}
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-sm text-slate-500">{field.description}</p>
        </button>

        {/* Icon-only on xs (labels stay in the DOM for a11y + text queries). */}
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <button
            type="button"
            className="btn-secondary inline-flex items-center gap-1 text-xs"
            onClick={() => setEditing(true)}
            disabled={busy || isRejected}
            title="Editar"
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden strokeWidth={2} />
            <span className="hidden sm:inline">Editar</span>
          </button>
          <button
            type="button"
            className="btn-secondary inline-flex items-center gap-1 text-xs text-emerald-700 hover:bg-emerald-50"
            onClick={() => onAccept(field.id)}
            disabled={busy || field.status === CandidateFieldStatuses.Accepted}
            title="Aceptar"
          >
            <Check className="h-3.5 w-3.5" aria-hidden strokeWidth={2.25} />
            <span className="hidden sm:inline">Aceptar</span>
          </button>
          <button
            type="button"
            className="btn-secondary inline-flex items-center gap-1 text-xs text-rose-700 hover:bg-rose-50"
            onClick={() => onReject(field.id)}
            disabled={busy || isRejected}
            title="Rechazar"
          >
            <X className="h-3.5 w-3.5" aria-hidden strokeWidth={2.25} />
            <span className="hidden sm:inline">Rechazar</span>
          </button>
        </div>
      </div>

      {expanded ? (
        <div className="border-t border-slate-100 bg-slate-50 px-4 py-3 text-sm">
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-slate-700 sm:grid-cols-2">
            <Detail label="Etiqueta cercana" value={field.evidence.nearbyLabel ?? '—'} />
            <Detail label="Ubicación" value={field.evidence.approximateLocation} />
            <Detail label="Página" value={String(field.evidence.pageNumber)} />
            <Detail
              label="Aparición"
              value={`${field.evidence.appearedInSampleCount} / ${field.evidence.totalSamples} (${Math.round(field.evidence.appearanceRate * 100)} %)`}
            />
            <Detail label="En tabla" value={field.evidence.isInTable ? 'Sí' : 'No'} />
            <Detail
              label="Clave-valor"
              value={field.evidence.isKeyValuePair ? 'Sí' : 'No'}
            />
          </dl>

          {field.aliases.length > 0 ? (
            <div className="mt-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Alias detectados
              </div>
              <div className="mt-1 flex flex-wrap gap-1">
                {field.aliases.map((alias) => (
                  <span
                    key={alias}
                    className="rounded bg-white px-2 py-0.5 font-mono text-xs text-slate-700 ring-1 ring-slate-200"
                  >
                    {alias}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          <div className="mt-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Razón de la sugerencia
            </div>
            <p className="mt-1 text-sm text-slate-700">{field.suggestionReason}</p>
          </div>

          {field.risks.length > 0 ? (
            <div className="mt-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                Riesgos
              </div>
              <ul className="mt-1 list-inside list-disc text-sm text-amber-900">
                {field.risks.map((risk, i) => (
                  <li key={`${field.id}-risk-${i}`}>{risk}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="mt-3">
            <label
              htmlFor={`hint-${field.id}`}
              className="text-xs font-semibold uppercase tracking-wide text-slate-500"
            >
              Pistas para la IA
            </label>
            <textarea
              id={`hint-${field.id}`}
              data-testid={`hint-textarea-${field.id}`}
              className="mt-1 w-full rounded border border-slate-300 bg-white p-2 font-mono text-xs text-slate-800 focus:border-sky-400 focus:outline-none focus:ring-1 focus:ring-sky-400"
              rows={3}
              maxLength={1000}
              value={hintDraft}
              onChange={(e) => setHintDraft(e.target.value)}
              onBlur={handleHintBlur}
              disabled={busy || isRejected}
              placeholder="Ej.: aparece en la cabecera, etiquetada como “IBAN:” cerca del logo del banco."
            />
            <p className="mt-1 text-xs text-slate-500">
              Este texto se envía al modelo cada vez que extrae este campo. Mejora la
              precisión cuando incluye etiquetas o ubicaciones típicas.
            </p>
          </div>

          {sourceField !== null ? (
            <div className="mt-3 rounded-md border border-indigo-100 bg-indigo-50/40 p-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700">
                Antes → Después (reeducación)
              </div>
              <dl className="mt-2 space-y-1 text-xs text-slate-700">
                {sourceField.type !== field.suggestedType ? (
                  <div className="flex gap-2">
                    <dt className="w-16 shrink-0 font-medium text-slate-500">Tipo</dt>
                    <dd>
                      <span className="text-rose-700 line-through">{sourceField.type}</span>
                      {' → '}
                      <span className="text-emerald-700">{field.suggestedType}</span>
                    </dd>
                  </div>
                ) : null}
                {sourceField.required !== field.suggestedRequired ? (
                  <div className="flex gap-2">
                    <dt className="w-16 shrink-0 font-medium text-slate-500">Requerido</dt>
                    <dd>
                      <span className="text-rose-700 line-through">
                        {sourceField.required ? 'sí' : 'no'}
                      </span>
                      {' → '}
                      <span className="text-emerald-700">
                        {field.suggestedRequired ? 'sí' : 'no'}
                      </span>
                    </dd>
                  </div>
                ) : null}
                {(sourceField.hint ?? '') !== (field.hint ?? '') ? (
                  <div className="flex gap-2">
                    <dt className="w-16 shrink-0 font-medium text-slate-500">Hint</dt>
                    <dd className="truncate">
                      <span className="text-rose-700 line-through">
                        {sourceField.hint ?? '—'}
                      </span>
                      {' → '}
                      <span className="text-emerald-700">{field.hint ?? '—'}</span>
                    </dd>
                  </div>
                ) : null}
              </dl>
            </div>
          ) : null}

          {field.isTableField ? (
            <CandidateFieldColumnsEditor
              value={field.columns ?? []}
              busy={busy || isRejected}
              onChange={(columns: CandidateTableColumnRequest[]) => onModify(field.id, { columns })}
            />
          ) : null}

          {field.suggestedType === 'Group' && (field.children?.length ?? 0) > 0 ? (
            <div className="mt-3 rounded-lg border border-indigo-100 bg-indigo-50/30 p-3">
              <div className="mb-2 flex items-center justify-between">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-indigo-900">
                  Sub-campos del grupo
                </h4>
                <span className="text-xs text-slate-500">
                  {field.children?.length} sub-campos
                </span>
              </div>
              <ul className="space-y-1.5">
                {field.children?.map((child) => (
                  <li
                    key={child.id}
                    className="flex flex-wrap items-center gap-2 rounded border border-slate-200 bg-white px-2 py-1.5 text-xs"
                  >
                    <span className="font-mono font-semibold text-slate-800">{child.functionalName}</span>
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-slate-600">
                      {child.suggestedType}
                    </span>
                    {child.suggestedRequired ? (
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">
                        obligatorio
                      </span>
                    ) : null}
                    {child.isCritical ? (
                      <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-medium text-rose-800">
                        crítico
                      </span>
                    ) : null}
                    {child.suggestionReason !== '' ? (
                      <span className="ml-auto truncate text-slate-500" title={child.suggestionReason}>
                        {child.suggestionReason}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-[10px] text-slate-500">
                Los sub-campos se editan tras publicar la nueva versión del esquema. Si quieres
                refinar la estructura del grupo ahora, rechaza el candidato y edita el schema
                manualmente.
              </p>
            </div>
          ) : null}
        </div>
      ) : null}

      <CandidateFieldEditModal
        open={editing}
        field={field}
        busy={busy}
        onSubmit={(request) => {
          onModify(field.id, request);
          setEditing(false);
        }}
        onCancel={() => setEditing(false)}
      />
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm text-slate-700">{value}</dd>
    </div>
  );
}
